import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Plus, GripVertical, Edit, Trash2, Clock, Link2, Unlink,
  MapPin, UserCircle2, Scroll, ChevronDown, ChevronRight, Filter, X
} from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ERAS = [
  { value: "ancient", label: "Ancient Era", color: "#78716c" },
  { value: "classical", label: "Classical Era", color: "#d4af37" },
  { value: "medieval", label: "Medieval Era", color: "#a78bfa" },
  { value: "modern", label: "Modern Era", color: "#3b82f6" },
  { value: "mythic", label: "Mythic Age", color: "#f59e0b" },
  { value: "dark", label: "Dark Age", color: "#dc2626" },
  { value: "golden", label: "Golden Age", color: "#d4af37" },
  { value: "custom", label: "Custom", color: "#9ca3af" },
];

const getEra = (val) => ERAS.find(e => e.value === val) || ERAS[ERAS.length - 1];

const WorldHistoryPage = () => {
  const { campaignId } = useParams();
  const confirm = useConfirm();
  const [campaign, setCampaign] = useState(null);
  const [events, setEvents] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: "", era: "medieval", date_label: "", description: "",
    linked_npcs: [], linked_locations: [], linked_quests: [],
    cause_event_id: null, effect_event_ids: [],
  });

  // Filter
  const [filterEra, setFilterEra] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [campRes, evtRes, npcRes, locRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/timeline`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCampaign(campRes.data);
      setEvents(evtRes.data);
      setNpcs(npcRes.data || []);
      setLocations(locRes.data || []);
    } catch {
      toast.error("Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setEditing(null);
    setFormData({
      title: "", era: "medieval", date_label: "", description: "",
      linked_npcs: [], linked_locations: [], linked_quests: [],
      cause_event_id: null, effect_event_ids: [],
    });
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (evt) => {
    setEditing(evt);
    setFormData({
      title: evt.title,
      era: evt.era || "custom",
      date_label: evt.date_label || "",
      description: evt.description || "",
      linked_npcs: evt.linked_npcs || [],
      linked_locations: evt.linked_locations || [],
      linked_quests: evt.linked_quests || [],
      cause_event_id: evt.cause_event_id || null,
      effect_event_ids: evt.effect_event_ids || [],
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) return;
    try {
      const payload = { ...formData, campaign_id: campaignId };
      if (editing) {
        await axios.put(`${API}/timeline/${editing.id}`, payload, { withCredentials: true });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/campaigns/${campaignId}/timeline`, payload, { withCredentials: true });
        toast.success("Event added to timeline");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch {
      toast.error("Failed to save event");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Event", "Remove this event from the timeline? Cause/effect links will be cleared.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/timeline/${id}`, { withCredentials: true });
      toast.success("Event deleted");
      if (expandedId === id) setExpandedId(null);
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(filteredEvents);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // Optimistic update
    const fullReordered = reordered.map((e, i) => ({ ...e, order: i }));
    setEvents(prev => {
      const filtered = prev.filter(e => filterEra !== "all" ? e.era !== filterEra : false);
      return [...fullReordered, ...filtered].sort((a, b) => a.order - b.order);
    });

    try {
      await axios.put(
        `${API}/campaigns/${campaignId}/timeline/reorder`,
        { event_ids: reordered.map(e => e.id) },
        { withCredentials: true }
      );
    } catch {
      toast.error("Failed to reorder");
      fetchData();
    }
  };

  // Link toggling
  const toggleLink = (field, id) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(id) ? prev[field].filter(x => x !== id) : [...prev[field], id],
    }));
  };

  const filteredEvents = filterEra === "all" ? events : events.filter(e => e.era === filterEra);

  // Helper to find event name
  const eventName = (id) => events.find(e => e.id === id)?.title || "Unknown";
  const npcName = (id) => npcs.find(n => n.id === id)?.name || "Unknown NPC";
  const locName = (id) => locations.find(l => l.id === id)?.name || "Unknown Location";

  if (loading) {
    return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner"></div></div></Layout>;
  }

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="world-history-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">World History</h1>
            <p className="text-muted-foreground text-sm">Chronicle the events that shaped your world</p>
          </div>
          <Button onClick={openCreate} data-testid="create-event-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> New Event
          </Button>
        </div>

        {/* Era filter */}
        {events.length > 0 && (
          <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="era-filter">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <button
              onClick={() => setFilterEra("all")}
              className={`px-3 py-1 rounded-sm text-xs border transition-colors ${
                filterEra === "all" ? "border-primary bg-primary/10 text-foreground" : "border-border/50 text-muted-foreground hover:text-foreground"
              }`}
              data-testid="filter-all"
            >
              All ({events.length})
            </button>
            {ERAS.filter(era => events.some(e => e.era === era.value)).map(era => (
              <button
                key={era.value}
                onClick={() => setFilterEra(era.value)}
                className={`px-3 py-1 rounded-sm text-xs border transition-colors ${
                  filterEra === era.value ? "border-primary bg-primary/10 text-foreground" : "border-border/50 text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`filter-${era.value}`}
              >
                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: era.color }} />
                {era.label} ({events.filter(e => e.era === era.value).length})
              </button>
            ))}
          </div>
        )}

        {events.length === 0 ? (
          <div className="empty-state">
            <Clock className="empty-state-icon text-muted-foreground" />
            <h3 className="text-xl font-serif text-foreground mb-2">No timeline events yet</h3>
            <p className="text-muted-foreground mb-6">Add events to build your world's history</p>
            <Button onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> Add First Event
            </Button>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="timeline">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="relative">
                  {/* Vertical timeline line */}
                  <div className="absolute left-6 top-0 bottom-0 w-px bg-border" />

                  {filteredEvents.map((evt, index) => {
                    const era = getEra(evt.era);
                    const isExpanded = expandedId === evt.id;
                    const causeEvent = evt.cause_event_id ? events.find(e => e.id === evt.cause_event_id) : null;
                    const effectEvents = (evt.effect_event_ids || []).map(id => events.find(e => e.id === id)).filter(Boolean);
                    const linkedNpcNames = (evt.linked_npcs || []).map(id => npcs.find(n => n.id === id)).filter(Boolean);
                    const linkedLocNames = (evt.linked_locations || []).map(id => locations.find(l => l.id === id)).filter(Boolean);

                    return (
                      <Draggable key={evt.id} draggableId={evt.id} index={index}>
                        {(prov, snapshot) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            className={`relative pl-14 pr-0 mb-4 transition-all ${snapshot.isDragging ? "z-50" : ""}`}
                            data-testid={`timeline-event-${evt.id}`}
                          >
                            {/* Timeline dot */}
                            <div
                              className="absolute left-4 top-4 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10"
                              style={{ borderColor: era.color, backgroundColor: `${era.color}22` }}
                            >
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: era.color }} />
                            </div>

                            {/* Event card */}
                            <div className={`bg-card border rounded-sm transition-all ${
                              snapshot.isDragging ? "border-primary shadow-lg ring-1 ring-primary/20" : "border-border hover:border-border/80"
                            }`}>
                              <div className="flex items-start gap-2 p-3">
                                {/* Drag handle */}
                                <div {...prov.dragHandleProps} className="mt-1 cursor-grab active:cursor-grabbing" data-testid={`drag-handle-${evt.id}`}>
                                  <GripVertical className="w-4 h-4 text-muted-foreground/50" />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-medium" style={{ backgroundColor: `${era.color}20`, color: era.color }}>
                                      {era.label}
                                    </span>
                                    {evt.date_label && (
                                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-2.5 h-2.5" /> {evt.date_label}
                                      </span>
                                    )}
                                  </div>

                                  <button
                                    onClick={() => setExpandedId(isExpanded ? null : evt.id)}
                                    className="w-full text-left mt-1 flex items-center gap-1.5"
                                    data-testid={`expand-event-${evt.id}`}
                                  >
                                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                                    <h3 className="font-serif text-base text-foreground truncate">{evt.title}</h3>
                                  </button>

                                  {/* Link badges (always visible) */}
                                  {(linkedNpcNames.length > 0 || linkedLocNames.length > 0 || causeEvent || effectEvents.length > 0) && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {linkedNpcNames.map(n => (
                                        <span key={n.id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-muted/30 border border-border/40 text-muted-foreground">
                                          <UserCircle2 className="w-2.5 h-2.5" /> {n.name}
                                        </span>
                                      ))}
                                      {linkedLocNames.map(l => (
                                        <span key={l.id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-muted/30 border border-border/40 text-muted-foreground">
                                          <MapPin className="w-2.5 h-2.5" /> {l.name}
                                        </span>
                                      ))}
                                      {causeEvent && (
                                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/10 border border-amber-500/20 text-amber-400">
                                          <Link2 className="w-2.5 h-2.5" /> Caused by: {causeEvent.title}
                                        </span>
                                      )}
                                      {effectEvents.map(ef => (
                                        <span key={ef.id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-500/10 border border-blue-500/20 text-blue-400">
                                          <Link2 className="w-2.5 h-2.5" /> Led to: {ef.title}
                                        </span>
                                      ))}
                                    </div>
                                  )}

                                  {/* Expanded description */}
                                  {isExpanded && evt.description && (
                                    <div className="mt-3 text-sm text-muted-foreground border-t border-border/30 pt-3">
                                      <RichTextDisplay content={evt.description} />
                                    </div>
                                  )}
                                </div>

                                {/* Actions */}
                                <div className="flex gap-0.5 shrink-0">
                                  <VisibilityToggle entityType="timeline_events" entityId={evt.id} currentVisibility={evt.visibility || "dm_only"} onUpdate={() => fetchData()} />
                                  <button onClick={() => openEdit(evt)} className="p-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid={`edit-event-${evt.id}`}>
                                    <Edit className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => handleDelete(evt.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-event-${evt.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); resetForm(); } }}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">{editing ? "Edit Event" : "New Timeline Event"}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-2 overflow-y-auto flex-1 pr-1">
              {/* Title */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5 block">Event Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="The Fall of Arathor"
                  data-testid="event-title-input"
                  className="bg-input border-border rounded-sm"
                />
              </div>

              {/* Era + Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5 block">Era</label>
                  <Select value={formData.era} onValueChange={(v) => setFormData({ ...formData, era: v })}>
                    <SelectTrigger data-testid="event-era-select" className="bg-input border-border rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ERAS.map(era => (
                        <SelectItem key={era.value} value={era.value}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: era.color }} />
                            {era.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5 block">Date / Year</label>
                  <Input
                    value={formData.date_label}
                    onChange={(e) => setFormData({ ...formData, date_label: e.target.value })}
                    placeholder="Year 342, Third Age"
                    data-testid="event-date-input"
                    className="bg-input border-border rounded-sm"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5 block">Description</label>
                <RichTextEditor
                  content={formData.description}
                  onChange={(val) => setFormData({ ...formData, description: val })}
                />
              </div>

              {/* Linked NPCs */}
              {npcs.length > 0 && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5 block">
                    Linked NPCs ({formData.linked_npcs.length})
                  </label>
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                    {npcs.map(n => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => toggleLink("linked_npcs", n.id)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-sm text-xs border transition-colors ${
                          formData.linked_npcs.includes(n.id)
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border/40 text-muted-foreground hover:border-border"
                        }`}
                        data-testid={`link-npc-${n.id}`}
                      >
                        <UserCircle2 className="w-3 h-3" /> {n.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Linked Locations */}
              {locations.length > 0 && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5 block">
                    Linked Locations ({formData.linked_locations.length})
                  </label>
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                    {locations.map(l => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => toggleLink("linked_locations", l.id)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-sm text-xs border transition-colors ${
                          formData.linked_locations.includes(l.id)
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border/40 text-muted-foreground hover:border-border"
                        }`}
                        data-testid={`link-location-${l.id}`}
                      >
                        <MapPin className="w-3 h-3" /> {l.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Cause & Effect — only show when other events exist */}
              {events.filter(e => e.id !== editing?.id).length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5 block">Caused By</label>
                    <Select
                      value={formData.cause_event_id || "none"}
                      onValueChange={(v) => setFormData({ ...formData, cause_event_id: v === "none" ? null : v })}
                    >
                      <SelectTrigger data-testid="cause-event-select" className="bg-input border-border rounded-sm">
                        <SelectValue placeholder="No cause" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="flex items-center gap-2"><Unlink className="w-3 h-3" /> No cause</span>
                        </SelectItem>
                        {events.filter(e => e.id !== editing?.id).map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5 block">
                      Led To ({formData.effect_event_ids.length})
                    </label>
                    <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                      {events.filter(e => e.id !== editing?.id).map(e => (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => toggleLink("effect_event_ids", e.id)}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] border transition-colors ${
                            formData.effect_event_ids.includes(e.id)
                              ? "border-blue-500/50 bg-blue-500/10 text-blue-300"
                              : "border-border/40 text-muted-foreground hover:border-border"
                          }`}
                          data-testid={`link-effect-${e.id}`}
                        >
                          <Link2 className="w-2.5 h-2.5" /> {e.title}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky save button at bottom */}
            <div className="pt-3 border-t border-border mt-2">
              <Button onClick={handleSubmit} disabled={!formData.title.trim()} data-testid="save-event-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                {editing ? "Save Changes" : "Add to Timeline"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WorldHistoryPage;
